import type {
  IngridCreateSessionResponse,
  OldProductDTO,
  Option,
  StorefrontOrderLine
} from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { ingridApi } from 'api'
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, getFullLocale } from 'shared-utils'
import { generateIngridCart } from 'api/utils/indrid/checkout/utils'
import { type ShippingProviderOpts } from '../types'
import { type CarrierContext } from './types'

/**
 * Opens the Ingrid session the order is created against.
 *
 * Answers with the same `{ error, data }` shape the PSP clients use, so the
 * pipeline can treat a carrier failure exactly like a payment failure. The
 * pages version took a callback and ran the rest of the order inside it, which
 * nested the whole flow one level deeper for no gain.
 */
export const openIngridSession = async (
  ctx: CarrierContext,
  opts: ShippingProviderOpts
): Promise<{ error?: Option<string>; data?: Option<IngridCreateSessionResponse> }> => {
  const { items, cartDiscountCode } = ctx.payload
  const cId = opts.customerId ?? undefined

  // Receiving products with all fields
  const fullProducts: OldProductDTO[] = await Promise.all(
    items.map(async (item) => {
      return storefrontApi.products.productById(item.product.id, cId)
    })
  ).then((products) => products.filter((p) => !!p))

  if (fullProducts.length !== items.length) {
    return { error: 'Products are not found' }
  }

  const fullItems: StorefrontOrderLine<OldProductDTO>[] = items.map((item, index) => ({
    ...item,
    product: fullProducts[index]
  }))

  return ingridApi.checkout.createSession({
    purchase_country:
      opts.shippingProviderConfig?.ingridOptions?.purchaseCountry || DEFAULT_COUNTRY,
    purchase_currency:
      opts.shippingProviderConfig?.ingridOptions?.purchaseCurrency || DEFAULT_CURRENCY,
    cart: generateIngridCart(fullItems, cartDiscountCode),
    locales: [getFullLocale(ctx.locale)]
  })
}
